import { useEffect, useMemo, useState } from "react";
import { type EChartsOption } from "echarts";
import styled from "styled-components";
import { numFormatter } from "../../../helpers/utils";
import BaseChart from "../../../components/baseChart";
import type { DimensionMetric } from "../types";

const PERIOD_COLORS = ["#2DB6F5", "#FF8A65", "#39C86A", "#E289F2"];

interface ComparisonDimensionChartProps {
  title: string;
  metrics: DimensionMetric[];
  width: number;
}

const ComparisonDimensionChart = ({ title, metrics, width }: ComparisonDimensionChartProps) => {
  const [activeMetric, setActiveMetric] = useState(0);
  const [option, setOption] = useState<EChartsOption>({});

  const chartHeight = useMemo(() => {
    if (width < 500) return 350;
    if (width < 800) return 450;
    return 500;
  }, [width]);

  const metric = metrics[activeMetric];

  useEffect(() => {
    if (!metric?.data?.series?.length) return;

    const seriesEntries = metric.data.series;
    const allCategories = [
      ...new Set(seriesEntries.flatMap((s) => s.series_data.map((d) => d.category)))
    ];
    const postfix = metric.config.value_formatter?.postfix || "";

    const echartsSeries = seriesEntries.map((entry, i) => {
      const valueMap = new Map(entry.series_data.map((d) => [d.category, d.value]));
      return {
        name: entry.name,
        type: "bar" as const,
        barGap: "10%",
        itemStyle: { color: PERIOD_COLORS[i % PERIOD_COLORS.length], borderRadius: [4, 4, 0, 0] },
        data: allCategories.map((cat) => valueMap.get(cat) ?? 0),
        emphasis: { itemStyle: { opacity: 0.85 } }
      };
    });

    setOption({
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        backgroundColor: "#fff",
        borderColor: "#e8e8e8",
        borderWidth: 1,
        textStyle: { color: "#333", fontSize: 12 },
        extraCssText: "border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,0.1)"
      },
      legend: {
        bottom: 0,
        itemGap: 16,
        icon: "circle",
        itemWidth: 10,
        itemHeight: 10,
        textStyle: { fontSize: 12 }
      },
      grid: {
        left: width < 650 ? "15%" : "8%",
        right: "4%",
        top: 30,
        bottom: 60,
        containLabel: false
      },
      xAxis: [
        {
          type: "category",
          data: allCategories,
          axisLine: { lineStyle: { color: "#e8e8e8" } },
          axisTick: { show: false },
          axisLabel: { fontSize: 11, color: "#666", rotate: allCategories.length > 6 ? 30 : 0 }
        }
      ],
      yAxis: [
        {
          type: "value",
          axisLabel: {
            fontSize: 11,
            formatter: (v: number) => `${numFormatter(v)}${postfix ? ` ${postfix}` : ""}`
          },
          splitLine: { lineStyle: { type: "dashed", color: "#e8e8e8" } },
          axisLine: { show: false },
          axisTick: { show: false }
        }
      ],
      series: echartsSeries,
      animationDuration: 600,
      animationEasing: "cubicOut"
    });
  }, [metric, width]);

  return (
    <Wrapper>
      {metrics.length > 1 && (
        <div className="metric-tabs">
          {metrics.map((m, i) => (
            <button
              key={m.config.name}
              className={`tab ${i === activeMetric ? "active" : ""}`}
              onClick={() => setActiveMetric(i)}
            >
              {m.config.name}
            </button>
          ))}
        </div>
      )}
      <BaseChart
        height={chartHeight}
        width={width}
        options={option}
        chartType="bar"
        onClick={() => {}}
      />
    </Wrapper>
  );
};

export default ComparisonDimensionChart;

const Wrapper = styled.div`
  .metric-tabs {
    display: flex;
    gap: 4px;
    margin-bottom: 16px;

    .tab {
      padding: 6px 16px;
      border: 1px solid ${({ theme }) => theme.border_color || "#ddd"};
      border-radius: 8px;
      background: transparent;
      color: ${({ theme }) => theme.text};
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.15s;

      &.active {
        background: #0d8a6a;
        color: #fff;
        border-color: #0d8a6a;
      }

      &:hover:not(.active) {
        background: ${({ theme }) => theme.body};
      }
    }
  }
`;
